import React, { useEffect, useRef, useState } from "react";
import { useGame } from "../context/game";
import { usePayment } from "../context/payment";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth";

export default function Game() {
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [loading, setLoading] = useState(true);

  const { recordDeath, recordEscape } = useGame();
  const { currentUser } = useAuth();
  const location = useLocation();
  const { hasPaid } = usePayment(); // if relevant

  // --- ROUTE GUARDING ---
  useEffect(() => {
    // If no state passed in, bounce back
    if (!location.state) {
      navigate("/");
    }
  }, [location, navigate]);

  useEffect(() => {
    // If not logged in, bounce back
    if (!currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  // --- REFS to track if we are currently on a “death” or “escape” screen ---
  // Using refs means we avoid tricky re-renders triggered by setState.
  const deathRef = useRef(false);
  const escapeRef = useRef(false);

  // This is your Twine HTML file path
  const src = location?.state?.demo ? "./DEMOKYEWB.html" : "./KYEWB.html";

  useEffect(() => {
    // This function is run once the iframe finishes loading (DOM ready)
    const handleIframeLoad = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return;

      // Phrases to detect
      const newGamePhrase1 = "You are being assigned a body";
      const newGamePhrase2 = "WAKEY WAKEY GREENHORN";

      const deathPhrase1 = "and wondering if next time you’ll win big";
      const deathPhrase2 = "START OVER";

      const escapePhraseDemo = "Father Pilnod's escape was easy enough";
      const escapePhraseMain = "Time to go home. You start walking";

      // This callback checks the text on **every** DOM change in the iframe
      const handleMutations = () => {
        const text = iframeDoc.body.innerText || "";

        // 1) Check if we see a new-game/respawn screen
        const isNewGame =
          text.includes(newGamePhrase1) || text.includes(newGamePhrase2);

        if (isNewGame) {
          // Clear everything so the next death/escape is recognized
          sessionStorage.removeItem("deathRecorded");
          sessionStorage.removeItem("escapeRecorded");
          deathRef.current = false;
          escapeRef.current = false;
          console.log("Detected new game / respawn in Twine!");
        }

        // 2) Detect DEATH if not on new-game text
        //    Some Twine versions never remove "START OVER" from the DOM, so
        //    we also require that we are NOT currently on new game.
        const isDeath =
          !isNewGame &&
          (text.includes(deathPhrase1) || text.includes(deathPhrase2));

        if (isDeath) {
          // If we haven't flagged a death screen yet...
          if (!deathRef.current) {
            const alreadyRecorded = sessionStorage.getItem("deathRecorded");
            if (!alreadyRecorded && !location.state?.demo) {
              console.log("Detected user DEATH in Twine. Recording now!");
              recordDeath();
              sessionStorage.setItem("deathRecorded", "true");
            }
            deathRef.current = true;
          }
        } else {
          // If the text no longer has the death phrase, reset the deathRef
          if (deathRef.current) {
            sessionStorage.removeItem("deathRecorded");
            deathRef.current = false;
          }
        }

        // 3) Detect ESCAPE
        const isEscape =
          text.includes(escapePhraseDemo) || text.includes(escapePhraseMain);

        if (isEscape) {
          if (!escapeRef.current) {
            const alreadyRecorded = sessionStorage.getItem("escapeRecorded");
            if (!alreadyRecorded && !location.state?.demo) {
              console.log("Detected user ESCAPE in Twine. Recording now!");
              recordEscape();
              sessionStorage.setItem("escapeRecorded", "true");
            }
            escapeRef.current = true;
          }
        } else {
          // If the text no longer has the escape phrase, reset the escapeRef
          if (escapeRef.current) {
            sessionStorage.removeItem("escapeRecorded");
            escapeRef.current = false;
          }
        }
      };

      // Setup one MutationObserver on the iframe's body
      const observer = new MutationObserver(handleMutations);
      observer.observe(iframeDoc.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Run once immediately in case something is already on screen
      handleMutations();

      // Cleanup if we unmount
      return () => observer.disconnect();
    };

    // Attach the 'load' event
    const iframeElement = iframeRef.current;
    if (iframeElement) {
      iframeElement.addEventListener("load", handleIframeLoad);
    }

    // Cleanup
    return () => {
      if (iframeElement) {
        iframeElement.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [recordDeath, recordEscape, location.state]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100vh" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            width: "100%",
            height: "100%",
            backgroundColor: "black",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            color: "white",
          }}
        >
          Loading...
        </div>
      )}

      <iframe
        ref={iframeRef}
        src={src}
        title="KYEWB Demo"
        style={{ width: "100%", height: "100%", border: "none" }}
        onLoad={() => setLoading(false)}
      />
    </div>
  );
}
